"use client";

import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";

interface SelectOption {
  value: string;
  label: string | React.ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  className?: string;
  customSelectStyle?: string;
  customClass?: string;
  isLocation?: boolean;
  dropdownPosition?: "top" | "bottom";
}

export const Select: React.FC<SelectProps> = ({
  value,
  onValueChange,
  placeholder = "Select...",
  options,
  disabled = false,
  className = "",
  customSelectStyle = "",
  customClass = "",
  isLocation = false,
  dropdownPosition = "bottom",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options?.find((opt) => opt?.value === value);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Optional: Auto-position based on available space
  useEffect(() => {
    if (isOpen && selectRef.current && dropdownRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - selectRect.bottom;
      const spaceAbove = selectRect.top;

      // If dropdownPosition is "bottom" but there's not enough space below,
      // auto-switch to top (optional feature)
      if (
        dropdownPosition === "bottom" &&
        spaceBelow < dropdownHeight &&
        spaceAbove > dropdownHeight
      ) {
        // You could set an internal state to track this
        // Or just let it overflow (current behavior)
      }
    }
  }, [isOpen, dropdownPosition]);

  const handleSelect = (option: SelectOption) => {
    if (!option.disabled) {
      onValueChange?.(option.value);
      setIsOpen(false);
    }
  };

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      {/* Button / Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((p) => !p)}
        className={clsx(
          "flex items-center justify-between w-full h-11 px-3 text-sm border rounded-md bg-white transition-colors duration-200",
          disabled
            ? "cursor-not-allowed opacity-50 bg-gray-50 text-gray-400 border-gray-200"
            : "cursor-pointer hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
          isOpen ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300",
          customClass
        )}
      >
        <span
          className={clsx(
            !value ? "text-gray-500" : "text-gray-900",
            isLocation && "location-select-item"
          )}
        >
          {!value ? placeholder : selectedOption?.label}
        </span>

        <ChevronDown
          className={clsx(
            "h-4 w-4 text-gray-500 transition-transform duration-200",
            isOpen && dropdownPosition === "bottom" && "rotate-180",
            isOpen && dropdownPosition === "top" && "-rotate-180"
          )}
        />
      </button>

      {/* Dropdown List */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={clsx(
            "absolute left-0 right-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg min-h-20 max-h-28 overflow-y-auto",
            dropdownPosition === "bottom" && "top-full mt-1",
            dropdownPosition === "top" && "bottom-full mb-1"
          )}
        >
          {options?.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option)}
              className={clsx(
                "flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors duration-200 text-gray-900 bg-white",
                customSelectStyle,
                option.disabled
                  ? "cursor-not-allowed opacity-50 text-gray-400"
                  : "hover:bg-gray-50",
                option.value === value
                  ? "bg-blue-50 text-blue-900"
                  : "text-gray-900"
              )}
            >
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Optional Shadcn-like wrappers (for consistent API usage) */
export const SelectTrigger = ({ children, className = "", ...props }: any) => (
  <div className={`relative ${className}`} {...props}>
    {children}
  </div>
);

export const SelectValue = ({ placeholder, children }: any) => (
  <span>{children || placeholder}</span>
);

export const SelectContent = ({
  children,
  className = "",
  position = "bottom",
}: any) => (
  <div
    className={clsx(
      "absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg",
      position === "bottom" && "top-full mt-1",
      position === "top" && "bottom-full mb-1",
      className
    )}
  >
    {children}
  </div>
);

export const SelectItem = ({
  value,
  children,
  disabled = false,
  ...props
}: any) => (
  <div
    className={clsx(
      "px-3 py-2 text-sm cursor-pointer transition-colors duration-200",
      disabled
        ? "cursor-not-allowed opacity-50 text-gray-400"
        : "hover:bg-gray-50"
    )}
    {...props}
  >
    {children}
  </div>
);
